import { useState } from "react";
import {
  WrapperField,
  useListController,
  ListContextProvider,
  TopToolbar,
  SelectColumnsButton,
  useRecordContext,
} from "react-admin";
import Button from "@/components/ui/Button";
import { ButtonStyleType, UIComponentSize } from "@/types/shared/uiComponents";
import { ChunkedExportButton } from "@/components/common/ChunkedExportButton";
import { getProps } from "@/utils/helpers/columnsHelper";
import { StyledDatagridConfigurable } from "@/components/ui/react-admin/StyledDatagrid";
import { PageHeader } from "@/components/common/PageHeader";
import List from "@/components/ui/react-admin/styledList/StyledList";
import { ListEmptyState } from "@/components/common/ListEmptyState";
import { getResourceIcon } from "@/utils/icons";
import { ResourceName } from "@/types";
import { DuplicateClaimantPairListAside } from "./components/DuplicateClaimantPairListAside";
import { DuplicateClaimantPairBulkActions } from "./components/DuplicateClaimantPairBulkActions";
import { DuplicateClaimantPairReviewDialog } from "./components/DuplicateClaimantPairReviewDialog";
import type { DuplicateClaimantPair } from "@/types/duplicateClaimantPair";
import {
  SeverityCell,
  StatusCell,
  ClaimantPairCell,
  ClientCell,
  CombinedSpendCell,
  WhyFlaggedCell,
  DetectionSourceCell,
} from "./components/DuplicateClaimantPairListCells";

/**
 * Duplicate claimant review list (Story #6717).
 *
 * The primary reconciliation pathway for suspected duplicate claimants. The
 * backend (#6518) also emits a thin task per pair, but the task is attention
 * ROUTING only — the judgement here is comparative, so a reviewer needs many
 * candidate pairs side by side rather than one task at a time.
 *
 * Structure follows ClaimantList: useListController + ListContextProvider, the
 * StyledList wrapper (not react-admin's bare List), PageHeader with a header
 * icon, StyledDatagridConfigurable for the column selector, and cells extracted
 * to components/. Column labels and tooltips come from getProps so they live in
 * allColumnsMetadata.json with every other resource rather than being hardcoded.
 *
 * SCOPE: the per-pair MERGE action is #6519 and is not here — that is the only
 * caveat still true. Bulk dismiss and the status/severity/client/found-by/reason
 * facets all ship in this slice.
 *
 * ORDERING is severity-first and every column is sortable={false}. The backend
 * sorts on a stored severity_ratio and deliberately exposes no creation-time
 * ordering: a queue sorted by arrival is precisely the failure this feature
 * exists to fix. Letting a user sort by another column would quietly reintroduce
 * it, so the columns are presentational only.
 */
export const DuplicateClaimantPairList = () => {
  const listContext = useListController({
    resource: ResourceName.DUPLICATE_CLAIMANT_PAIRS,
    // filterDefaultValues, NOT filter. In react-admin `filter` is a PERMANENT
    // filter the user cannot override -- setting status there silently killed the
    // aside's Status group, so "Dismissed" and "Merged" appeared clickable but
    // could never change the result. A default opens the queue on Open while
    // leaving the other statuses reachable.
    filterDefaultValues: { status: "open" },
    sort: { field: "severity_ratio", order: "DESC" },
    perPage: 25,
  });
  const { isPending: loading } = listContext;
  // Row click opens the side-by-side review rather than navigating: the decision
  // is a comparison, and sending the reviewer to a detail page would lose the
  // other half of the pair.
  const [reviewing, setReviewing] = useState<DuplicateClaimantPair | null>(null);
  const DuplicatesIcon = getResourceIcon(ResourceName.DUPLICATE_CLAIMANT_PAIRS);

  return (
    <ListContextProvider value={listContext}>
      <PageHeader
        customTitle="Duplicate Claimants"
        customSubtitle="Claimant records that may be the same person, ranked by combined spend against the specific deductible."
        hideSectionTitle
        headerIcon={<DuplicatesIcon sx={{ fontSize: 28, color: "primary.main" }} />}
        rightContent={<DuplicateClaimantPairListActions loading={loading} />}
      />
      <List
        actions={false}
        aside={<DuplicateClaimantPairListAside />}
        perPage={25}
        sort={{ field: "severity_ratio", order: "DESC" }}
        loading={loading}
        empty={
          <ListEmptyState
            resource={ResourceName.DUPLICATE_CLAIMANT_PAIRS}
            message="No suspected duplicates"
            subtitle="Claimant records that may be the same person will appear here for review"
          />
        }
      >
        <StyledDatagridConfigurable
          // Selection checkboxes + bulk dismiss. No bulk MERGE, ever — dismissal
          // changes no financial record, a merge moves money and needs a per-pair
          // impact preview (#6519).
          bulkActionButtons={<DuplicateClaimantPairBulkActions />}
          rowClick={(_id, _resource, record) => {
            setReviewing(record as unknown as DuplicateClaimantPair);
            return false;
          }}
          omit={["detection_source"]}
        >
          <WrapperField {...getProps("severity", ResourceName.DUPLICATE_CLAIMANT_PAIRS)} sortable={false}>
            <SeverityCell />
          </WrapperField>

          <WrapperField {...getProps("status", ResourceName.DUPLICATE_CLAIMANT_PAIRS)} sortable={false}>
            <StatusCell />
          </WrapperField>

          <WrapperField {...getProps("client_id", ResourceName.DUPLICATE_CLAIMANT_PAIRS)} sortable={false}>
            <ClientCell />
          </WrapperField>

          <WrapperField {...getProps("claimant_pair", ResourceName.DUPLICATE_CLAIMANT_PAIRS)} sortable={false}>
            <ClaimantPairCell />
          </WrapperField>

          <WrapperField
            {...getProps("combined_paid_amount", ResourceName.DUPLICATE_CLAIMANT_PAIRS)}
            textAlign="right"
            sortable={false}
          >
            <CombinedSpendCell />
          </WrapperField>

          <WrapperField {...getProps("detection_reason", ResourceName.DUPLICATE_CLAIMANT_PAIRS)} sortable={false}>
            <WhyFlaggedCell />
          </WrapperField>

          {/* Hidden by default — available via the column selector */}
          <WrapperField {...getProps("detection_source", ResourceName.DUPLICATE_CLAIMANT_PAIRS)} sortable={false}>
            <DetectionSourceCell />
          </WrapperField>

          {/* The row is one click target with client and claimant LINKS inside
              it, so "click the row" sent reviewers to a record page as often as
              to the review (#7224). An action-named button is the affordance;
              the row click stays as the large target. */}
          <WrapperField label="Review" source="review" sortable={false}>
            <ReviewCell onReview={setReviewing} />
          </WrapperField>
        </StyledDatagridConfigurable>
      </List>
      {/* key={reviewing.id} REMOUNTS the dialog per pair, which is what resets its
          form state. Without it the instance survives a close -- `if (!pair) return
          null` sits after the hooks, and this parent renders the component
          unconditionally -- so a dismissal reason typed for one pair could be
          recorded against the next one opened (review !4630 thread 27545). It also
          covers the failed-action path, where the reason previously survived too. */}
      <DuplicateClaimantPairReviewDialog
        key={reviewing?.id ?? "none"}
        pair={reviewing}
        onClose={() => setReviewing(null)}
      />
    </ListContextProvider>
  );
};

const ReviewCell = ({ onReview }: { onReview: (pair: DuplicateClaimantPair) => void }) => {
  const record = useRecordContext<DuplicateClaimantPair>();
  if (!record) return null;
  return (
    <Button
      size={UIComponentSize.Small}
      variant={ButtonStyleType.Outlined}
      onClick={(e) => {
        e.stopPropagation();
        onReview(record);
      }}
      data-testid={`review-pair-${record.id}`}
    >
      Review
    </Button>
  );
};

/**
 * Top toolbar. Same composition as ClaimantsListActions minus Create — pairs are
 * detected by the matcher and the sweep, never authored by a user, so a Create
 * button would be a lie.
 */
const DuplicateClaimantPairListActions = ({ loading }: { loading?: boolean }) => (
  <TopToolbar>
    <SelectColumnsButton disabled={loading} />
    <ChunkedExportButton disabled={loading} />
  </TopToolbar>
);
